import { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { ChevronLeft, ChevronRight, Plus, Trash2, Copy, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function mondayOf(iso) {
  const d = new Date(iso + "T00:00:00");
  const dow = d.getDay(); const offset = dow === 0 ? 6 : dow - 1;
  d.setDate(d.getDate() - offset);
  return d.toISOString().slice(0, 10);
}

export default function CampaignPlannerView({ campaigns, userNameMap, onOpenShift, onRefresh }) {
  const [campaignId, setCampaignId] = useState(campaigns?.[0]?.id || "");
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date().toISOString().slice(0, 10)));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!campaignId || !weekStart) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/campaigns/${campaignId}/planner?week_start=${weekStart}`);
      setData(res.data);
    } catch (e) {
      setData(null);
      toast.error(e.response?.data?.detail || "Failed to load campaign planner");
    } finally {
      setLoading(false);
    }
  }, [campaignId, weekStart]);

  useEffect(() => { load(); }, [load]);

  const weekDates = data?.weekDates || [];
  const locations = data?.locations || [];

  const cellShifts = (locId, date) => (data?.grid?.[`${locId}|${date}`] || []);

  const gotoWeek = (delta) => setWeekStart(addDays(weekStart, delta * 7));

  // Quick action: copy Monday across to Tue-Fri (per location row)
  const copyMondayAcross = async (locationId) => {
    const monday = weekDates[0];
    const mondaysShifts = cellShifts(locationId, monday).filter(s => s.isCampaignMatch);
    if (mondaysShifts.length === 0) { toast.error("No Monday shifts to copy for this row"); return; }
    let created = 0;
    for (let i = 1; i <= 4; i++) {
      const target = weekDates[i];
      for (const s of mondaysShifts) {
        try {
          await apiClient.post("/schedules", {
            userIds: s.userIds || [],
            locationId, campaignId, date: target,
            startTime: s.startTime, endTime: s.endTime,
          });
          created++;
        } catch (e) { /* skip clashes */ }
      }
    }
    toast.success(`Copied ${created} shifts across the week`);
    load(); onRefresh?.();
  };

  // Quick action: autofill weekdays (Mon-Fri) with a default 10:00-16:00 shift using first assigned fundraiser from Monday if any, else empty
  const autofillWeekdays = async (locationId) => {
    const monday = weekDates[0];
    const mondayShifts = cellShifts(locationId, monday);
    const userIds = mondayShifts[0]?.userIds || [];
    let created = 0;
    for (let i = 0; i < 5; i++) {
      try {
        await apiClient.post("/schedules", {
          userIds, locationId, campaignId, date: weekDates[i],
          startTime: "10:00", endTime: "16:00",
        });
        created++;
      } catch (e) { /* skip duplicates */ }
    }
    toast.success(`Autofilled ${created} weekday shifts`);
    load(); onRefresh?.();
  };

  // Clear row
  const clearRow = async (locationId) => {
    if (!window.confirm("Delete all shifts for this location this week?")) return;
    try {
      const res = await apiClient.post(`/campaigns/${campaignId}/planner/clear-row`, {
        locationId, weekDates
      });
      toast.success(`Cleared ${res.data.deleted} shift${res.data.deleted === 1 ? "" : "s"}`);
      load(); onRefresh?.();
    } catch (e) {
      toast.error("Failed to clear row");
    }
  };

  // Copy previous week forward
  const copyPreviousWeek = async () => {
    const prev = addDays(weekStart, -7);
    try {
      const r = await apiClient.get(`/campaigns/${campaignId}/planner?week_start=${prev}`);
      const prevGrid = r.data?.grid || {};
      let created = 0;
      for (const key of Object.keys(prevGrid)) {
        const [locId, date] = key.split("|");
        const targetDate = addDays(date, 7);
        for (const s of prevGrid[key]) {
          if (!s.isCampaignMatch) continue;
          try {
            await apiClient.post("/schedules", {
              userIds: s.userIds || [], locationId: locId, campaignId,
              date: targetDate, startTime: s.startTime, endTime: s.endTime
            });
            created++;
          } catch (e) { /* skip clash */ }
        }
      }
      toast.success(`Copied ${created} shift${created === 1 ? "" : "s"} from previous week`);
      load(); onRefresh?.();
    } catch (e) {
      toast.error("Failed to copy previous week");
    }
  };

  const campaign = useMemo(() => campaigns.find(c => c.id === campaignId), [campaigns, campaignId]);

  return (
    <div className="space-y-4" data-testid="campaign-planner-view">
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div>
            <div className="text-xs text-slate-500 mb-1">Campaign</div>
            <select
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm min-w-[220px]"
              data-testid="campaign-planner-select"
            >
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => gotoWeek(-1)} data-testid="cp-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="px-3 py-1.5 bg-slate-100 rounded text-xs font-medium" data-testid="cp-week-label">
              <CalendarDays className="h-3 w-3 inline mr-1" />
              Week of {weekStart}
            </div>
            <Button variant="outline" size="sm" onClick={() => gotoWeek(1)} data-testid="cp-next-week"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <Button variant="outline" size="sm" onClick={copyPreviousWeek} data-testid="cp-copy-previous"><Copy className="h-3.5 w-3.5 mr-1" />Copy previous week</Button>
          {campaign && <Badge variant="outline" className="ml-auto">{locations.length} linked location{locations.length === 1 ? "" : "s"}</Badge>}
        </CardContent>
      </Card>

      {loading && <div className="text-center py-6 text-slate-500">Loading planner...</div>}
      {!loading && data && (
        <div className="overflow-x-auto" data-testid="campaign-planner-grid">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                <th className="text-left p-2 border border-slate-200 sticky left-0 bg-slate-50 min-w-[180px]">Location</th>
                {DAYS.map((d, i) => (
                  <th key={d} className="p-2 border border-slate-200 text-left min-w-[140px]">
                    <div>{d}</div>
                    <div className="text-[10px] text-slate-400 font-normal">{weekDates[i]?.slice(5)}</div>
                  </th>
                ))}
                <th className="p-2 border border-slate-200 min-w-[140px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {locations.length === 0 && (
                <tr><td colSpan={9} className="p-4 text-center text-slate-400">No locations linked to this campaign</td></tr>
              )}
              {locations.map(loc => (
                <tr key={loc.id} data-testid={`cp-row-${loc.id}`} className="align-top">
                  <td className="p-2 border border-slate-200 font-medium text-slate-900 sticky left-0 bg-white">{loc.name}</td>
                  {weekDates.map((date, i) => (
                    <td key={date} className="p-1.5 border border-slate-200" data-testid={`cp-cell-${loc.id}-${i}`}>
                      <div className="space-y-1">
                        {cellShifts(loc.id, date).map(s => (
                          <div key={s.id} className={`rounded px-1.5 py-1 text-[11px] ${s.isCampaignMatch ? "bg-amber-50 border border-amber-200" : "bg-slate-50 border border-slate-200 opacity-70"}`}>
                            <div className="font-mono text-slate-700">{s.startTime}-{s.endTime}</div>
                            <div className="truncate text-slate-600">
                              {(s.userIds || []).length === 0 ? <span className="italic text-slate-400">Unassigned</span> : (s.userIds || []).map(uid => userNameMap?.[uid] || uid.slice(0,4)).join(", ")}
                            </div>
                            {!s.isCampaignMatch && <span className="text-[9px] text-slate-400">(other campaign)</span>}
                          </div>
                        ))}
                        <Button
                          variant="ghost" size="sm"
                          className="h-6 w-full text-[11px] text-slate-500 hover:text-slate-900"
                          onClick={() => onOpenShift?.({ locationId: loc.id, campaignId, date })}
                          data-testid={`cp-add-${loc.id}-${i}`}
                        >
                          <Plus className="h-3 w-3 mr-0.5" /> Add
                        </Button>
                      </div>
                    </td>
                  ))}
                  <td className="p-1.5 border border-slate-200">
                    <div className="flex flex-col gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => copyMondayAcross(loc.id)} data-testid={`cp-copy-monday-${loc.id}`}>Copy Mon→Fri</Button>
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => autofillWeekdays(loc.id)} data-testid={`cp-autofill-${loc.id}`}>Autofill weekdays</Button>
                      <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-600 hover:text-rose-700" onClick={() => clearRow(loc.id)} data-testid={`cp-clear-${loc.id}`}><Trash2 className="h-3 w-3 mr-1" />Clear row</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
